import TrackingEvent from 'constants/trackingEvents';
import { useRouter } from 'next/router';
import { AscDesc, Channel, ChannelMemberResponse } from 'stream-chat';
import { ChannelList, ChannelListMessengerProps } from 'stream-chat-react';
import { conversationFilter } from 'utils/new/conversationFilter';
import { goToConversation } from 'utils/routerHelpers';

import React, { useMemo } from 'react';

import { Badge } from '@thinkific/chalk/Badge';
import { MenuItemWithSymbols } from '@thinkific/chalk/recipes/MenuItemWithSymbols';
import { classNames } from '@thinkific/chalk/utils';

import { useCommunityContext } from 'components/CommunityContext';
import UserAvatar from 'components/UserAvatar';
import NavList from 'components/new/ui/MenuList/MenuList';

import { useChatContext } from '../providers/ChatProvider/ChatProvider';
import * as css from './ConversationsList.css';

type ListContainerProps = ChannelListMessengerProps & {
  children: React.ReactNode;
  props?: ChannelListMessengerProps;
};

const ListContainer = ({ props, children }: ListContainerProps) => {
  return <NavList {...props}>{children}</NavList>;
};

const ChannelItem = ({
  channel,
  activeChannel,
  displayImage,
  displayTitle,
  unread,
  lastMessage,
  displayedMessage,
  captionLength,
}) => {
  const isActiveChannel = channel.id === activeChannel?.id;
  const router = useRouter();
  const { community, trackEvent, currentUser } = useCommunityContext();
  const handleChannelClick = (channelId: string) => {
    trackEvent(TrackingEvent.DIRECT_MESSAGES_CONVERSATION_CLICKED);
    goToConversation(router, community.id, channelId);
  };

  const hasOnlineMember = useMemo(
    () =>
      Object.values(channel.state?.members || {})
        .filter((member: ChannelMemberResponse) => member.user?.id !== currentUser?.gid)
        .some((member: ChannelMemberResponse) => member.user?.online),
    [channel.state?.members]
  );

  const caption = useMemo(() => {
    const text =
      (lastMessage?.text || '')?.length > captionLength
        ? `${lastMessage?.text?.slice(0, captionLength)}...`
        : lastMessage?.text;
    if (!text) return undefined;
    if (displayedMessage === 'none') return undefined;
    if (displayedMessage === 'unread') return unread ? text : undefined;
    return text;
  }, [displayedMessage, unread, lastMessage]);

  return (
    <MenuItemWithSymbols
      active={isActiveChannel}
      caption={caption}
      className={css.channelItem}
      dataQa="communities-conversations-list-item"
      endSymbol={
        unread > 0 && (
          <Badge className={css.unreadBadge} label={unread} roundness="full" variant="primary" />
        )
      }
      label={displayTitle}
      leadSymbol={
        <UserAvatar
          avatarUrl={displayImage}
          showOnlineIndicator={hasOnlineMember}
          size="xs"
          userId={channel.id}
          username={displayTitle}
        />
      }
      onClick={() => handleChannelClick(channel.id)}
    />
  );
};

type ConversationsListProps = {
  variant?: 'default' | 'raised';
  displayedMessage?: 'none' | 'unread' | 'lastMessage';
  captionLength?: number;
};

const ConversationsList = ({
  variant = 'default',
  displayedMessage = 'unread',
  captionLength,
}: ConversationsListProps) => {
  const { session, baseChannelFilters } = useChatContext();
  if (!session || !session.user) return null;

  const filters = {
    ...baseChannelFilters,
    members: { $in: [session.user.id] },
  };

  const channelRenderFilterFn = (channel: Channel) => conversationFilter(channel, filters, session);

  const sort = { last_message_at: -1 as AscDesc };
  const Element = (props) => (
    <ChannelItem {...props} captionLength={captionLength} displayedMessage={displayedMessage} />
  );

  return (
    <div className={classNames(css.channelList[variant])}>
      <ChannelList
        channelRenderFilterFn={(channels) => channels.filter(channelRenderFilterFn)}
        EmptyStateIndicator={() => null}
        filters={filters}
        List={ListContainer}
        Preview={Element}
        setActiveChannelOnMount={false}
        sort={sort}
      />
    </div>
  );
};

export default ConversationsList;
